<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="ac-header">Performance Score</div>
      <button
        type="button"
        class="ac-close-button"
        @click="toggleActionModal(false)"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          alt="x"
          class="ac-close"
        >
      </button>
    </div>
    <div class="ac-body-container ps-body">
      <div class="ps-tabs">
        <button
          type="button"
          :class="['ps-tab', activeTab === 'cooling' ? 'ps-tab-active' : '']"
          @click="activeTab = 'cooling'"
        >
          Cooling
        </button>
        <button
          type="button"
          :class="['ps-tab', activeTab === 'heating' ? 'ps-tab-active' : '']"
          @click="activeTab = 'heating'"
        >
          Heating
          <span v-if="heatingWarning" class="ps-warning">!</span>
        </button>
      </div>

      <div v-if="!activeScore" class="ps-empty">
        <span class="sac__font-14 n70 med">No {{ activeTab }} scores available</span>
      </div>

      <template v-else>
        <div class="ps-score-header">
          <span class="sac__font-14 med">Performance Score</span>
          <span class="sac__font-14 n70">
            Tested {{ formattedDate(activeScore.timestamp) }}
          </span>
        </div>

        <div class="ps-donut-container">
          <img
            v-if="activeGrade"
            :src="gradeIcon"
            :alt="`Grade ${activeGrade}`"
            class="ps-grade-img"
          />
        </div>

        <div class="ps-summary sac__font-14 n70">
          Based on system efficiency test,
          {{ gradeSummary }}
        </div>

        <div v-if="previousScore" class="ps-previous">
          <div class="ps-divider" />
          <div class="ps-previous-row">
            <span class="sac__font-14 n70">
              Previously tested on {{ formattedDate(previousScore.timestamp) }}
            </span>
            <span :class="['sac__font-14 semi-bold', previousGradeColor]">
              {{ previousGrade }}
            </span>
          </div>
        </div>

        <div class="ps-info-box">
          <span class="sac__font-14 med">Recommendation:</span>
          <span class="sac__font-14 n70">
            If system receives a C or D score, we recommend scheduling service
            with your provider.
          </span>
        </div>
      </template>
    </div>
    <div class="ac-footer-container">
      <div />
      <button
        type="button"
        class="sac__button-sm bg-primary"
        @click="toggleActionModal(false)"
      >
        Close
      </button>
    </div>
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';

import gradeA from '@shared/assets/img/performance-score/grade-a.svg';
import gradeB from '@shared/assets/img/performance-score/grade-b.svg';
import gradeC from '@shared/assets/img/performance-score/grade-c.svg';
import gradeD from '@shared/assets/img/performance-score/grade-d.svg';

const GRADE_ICONS = { A: gradeA, B: gradeB, C: gradeC, D: gradeD };

const GRADE_SUMMARIES = {
  A: 'the system performed well.',
  B: 'the system performed well with small gaps from ideal performance.',
  C: 'the system did not perform at its best. A professional check is recommended.',
  D: 'the system performed poorly. Professional service is strongly recommended.',
};

export default {
  name: 'PerformanceScoreModal',
  mixins: [customerDetailMixin],
  data() {
    return {
      activeTab: 'cooling',
    };
  },
  computed: {
    coolingData() {
      return this.performanceScores?.cooling || null;
    },
    heatingData() {
      return this.performanceScores?.heating || null;
    },
    activeData() {
      return this.activeTab === 'cooling' ? this.coolingData : this.heatingData;
    },
    activeScore() {
      return this.activeData?.latest || null;
    },
    previousScore() {
      const historical = this.activeData?.historical;
      return historical && historical.length ? historical[0] : null;
    },
    activeGrade() {
      return this.activeScore?.grade || '';
    },
    previousGrade() {
      return this.previousScore?.grade || '';
    },
    previousGradeColor() {
      return this.gradeColorClass(this.previousGrade);
    },
    gradeSummary() {
      return GRADE_SUMMARIES[this.activeGrade] || '';
    },
    gradeIcon() {
      return GRADE_ICONS[this.activeGrade] || '';
    },
    heatingWarning() {
      const grade = this.heatingData?.latest?.grade;
      return grade === 'C' || grade === 'D';
    },
  },
  methods: {
    gradeColorClass(letter) {
      if (letter === 'A') return 'f-green';
      if (letter === 'B') return 'f-primary';
      if (letter === 'C') return 'f-yellow';
      if (letter === 'D') return 'tomato';
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
.ps-body {
  gap: 16px;
}

.ps-tabs {
  display: flex;
  gap: 8px;
  background-color: $NBLUE5;
  border-radius: 8px;
  padding: 4px;
}

.ps-tab {
  flex: 1;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: $N70;
  background: transparent;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: $NBLUE10;
  }

  &.ps-tab-active {
    background-color: $WHITE;
    color: $N100;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.ps-warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: $TOMATO100;
  color: $WHITE;
  font-size: 11px;
  font-weight: 700;
  margin-left: 4px;
}

.ps-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}

.ps-score-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ps-donut-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
}

.ps-grade-img {
  width: 100px;
  height: 100px;
}

.ps-summary {
  text-align: center;
  line-height: 1.5;
}

.ps-previous {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ps-divider {
  height: 1px;
  background-color: $NBLUE10;
}

.ps-previous-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ps-info-box {
  background-color: $NBLUE5;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
